import { motion } from "framer-motion";
import { ScanLine, CreditCard, Globe, ArrowRight } from "lucide-react";
import { ASSETS, HERO_CHIPS } from "../../data/arccaContent";

const CHIP_ICONS = { ScanLine, CreditCard, Globe };

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const ArccaHero = () => (
  <section id="acasa" data-testid="arcca-hero" className="relative overflow-hidden bg-white pt-28 pb-16 lg:pt-36 lg:pb-28">
    {/* soft gradient backdrop — kept minimal for a clean, premium feel */}
    <div className="pointer-events-none absolute inset-0 -z-0">
      <div className="absolute -top-28 -left-20 h-[440px] w-[440px] rounded-full bg-arcca/15 blur-3xl" />
      <div className="absolute top-0 right-0 h-[480px] w-[480px] rounded-full bg-arccablue/10 blur-3xl" />
    </div>

    <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
      <div className="lg:col-span-6">
        <motion.h1
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-extrabold tracking-tight text-arccaink leading-[1.03] text-4xl sm:text-5xl lg:text-6xl"
        >
          Noul tău card de acces.
          <span className="block text-arcca-dark">Și mult mai mult de atât.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.12 }}
          className="mt-6 max-w-xl text-base md:text-lg text-slate-600 leading-relaxed"
        >
          Arcca face trecerea către o nouă experiență de acces în campus, împreună cu BRD și ISIC.
          Cu BRD-ISIC, poți folosi același card pentru accesul prin turnicheții compatibili, pentru
          plățile de zi cu zi și pentru mii de beneficii dedicate studenților.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22 }}
          className="mt-7 flex flex-wrap gap-2.5"
        >
          {HERO_CHIPS.map((chip) => {
            const Icon = CHIP_ICONS[chip.icon];
            return (
              <span
                key={chip.label}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3.5 py-1.5 text-sm font-medium text-arccaink"
              >
                <Icon size={15} className="text-arcca-dark" strokeWidth={2.2} />
                {chip.label}
              </span>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.32 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("ce-se-schimba")}
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 rounded-full bg-arccaink px-7 py-3.5 text-sm font-display font-bold text-white hover:bg-black transition-colors"
          >
            Descoperă cum funcționează
            <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
          </button>
          <button
            onClick={() => scrollTo("beneficii")}
            data-testid="hero-cta-secondary"
            className="inline-flex items-center rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-display font-bold text-arccaink hover:border-arcca hover:text-arcca-dark transition-colors"
          >
            Vezi beneficiile ISIC
          </button>
        </motion.div>
      </div>

      {/* Clean card visual — no overlaid labels */}
      <div className="lg:col-span-6 flex justify-center lg:justify-end">
        <motion.div
          initial={{ opacity: 0, y: 40, rotate: -6 }}
          animate={{ opacity: 1, y: 0, rotate: -4 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <motion.img
            src={ASSETS.card}
            alt="Cardul BRD-ISIC"
            data-testid="hero-card-image"
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="w-full rounded-3xl shadow-2xl shadow-arccaink/25 ring-1 ring-black/5"
          />
        </motion.div>
      </div>
    </div>
  </section>
);
